import * as React from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Filter,
  Home,
  KeyRound,
  Megaphone,
  MousePointerClick,
  Plus,
  Search,
  Undo2,
  User as UserIcon,
  X,
} from 'lucide-react';
import type {
  AffiliateSettings,
  AffiliateSettingsUpdateInput,
  BannerSlogans,
  ListingRow,
  ProfileButton,
  ProptreeEnumValue,
} from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { AddProptreeButtonModal } from '../components/AddProptreeButtonModal.js';
import {
  BannerSlogansEditor,
  cleanBannerSlogans,
  defaultBannerSlogans,
} from '../components/BannerSlogansEditor.js';
import { ImageUploadButton } from '../components/ImageUploadButton.js';
import { ProptreeButtonRow } from '../components/ProptreeButtonRow.js';
import {
  ProptreePreview,
  type ProptreePreviewPatch,
} from '../components/ProptreePreview.js';
import { SectionCard } from '../components/SectionCard.js';
import { PartnerToggleList } from '../components/PartnerToggleList.js';
import { AppShell, useBannerSlot } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { isTestMode, withDemoFlag } from '../lib/testMode.js';
import { cn, initials } from '../lib/utils.js';
import { COUNTRY_CODES } from '../lib/countryCodes.js';
import { client } from '../main.js';

// Where public affiliate pages render — same as the agency settings page.
const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  'http://localhost:4040';

// Legacy-shaped enum option lists — mirrors the agency settings page and
// the legacy createNewProptree.js modal. Keep these in sync if either
// side gains a new option.
const EXTERNAL_ACCESS_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Disabled', value: 'disabled' },
  { id: 2, name: 'Enabled', value: 'enabled' },
];

const MAIN_FEED_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Enabled', value: 'enabled' },
  { id: 2, name: 'Disabled', value: 'disabled' },
];

const PROPERTIES_TYPE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Sale & Rentals', value: 'both' },
  { id: 2, name: 'Sale', value: 'sale' },
  { id: 3, name: 'Rentals', value: 'rent' },
];

const PROPERTY_PLAN_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'On-plan & Off-plan', value: 'both' },
  { id: 2, name: 'On-plan', value: 'on_plan' },
  { id: 3, name: 'Off-plan', value: 'off_plan' },
];

const CHOOSE_LISTINGS_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'All listings', value: 'disabled' },
  { id: 2, name: 'Specific', value: 'enabled' },
];

// Banner-card option lists (mirrors the agency settings page so the
// dropdown semantics stay identical).
const AGENCY_BANNER_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Disabled', value: 'disabled' },
  { id: 2, name: 'Enabled', value: 'enabled' },
];

const BANNER_STATS_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Show stats', value: 'enabled' },
  { id: 2, name: 'Hide stats', value: 'disabled' },
];

// Tabs the BannerCard renders, in display order.
type BannerTab = 'top' | 'card' | 'newsletter';
const BANNER_TAB_LABELS: Record<BannerTab, string> = {
  top: 'Banner · Slogans',
  card: 'Banner · Agency',
  newsletter: 'Newsletter',
};

const BUTTONS_MAX = 20;

// Page-owned editable subset of the saved AffiliateSettings. The `feed`
// field is held as a ProptreeEnumValue (matched against formOptions) so
// the EnumSelect can drive it; we convert back to the legacy `{ id, name }`
// shape at save time. Everything else round-trips unchanged.
type FormState = {
  alias: string;
  profileImage: string | null;
  profileFirstName: string;
  profileSurName: string;
  profileCompany: string;
  buttons: ProfileButton[];
  externalAccess: ProptreeEnumValue | null;
  whatsAppCountryCode: ProptreeEnumValue | null;
  whatsAppNumber: string;
  mainFeed: ProptreeEnumValue | null;
  propertiesType: ProptreeEnumValue | null;
  property: ProptreeEnumValue | null;
  feed: ProptreeEnumValue | null;
  chooseListings: ProptreeEnumValue | null;
  listings: Record<string, boolean>;
  // Branded partner toggles. Always held as a plain map so dirty diffs
  // can compare via JSON; null on the doc collapses to {} here.
  partners: Record<string, boolean>;
  // Top-marquee slogans (enabled + presets + customs). Defaults to a
  // disabled-all-presets-empty-customs shape when the doc has nothing
  // set so the editor always has a renderable target.
  bannerSlogans: BannerSlogans;
  // Decorative banner card between listings (separate from the marquee
  // strip). The agency-page version is named `showAgencyBanner` even
  // when used on an affiliate page; field name kept verbatim so reader
  // code stays compatible.
  showAgencyBanner: ProptreeEnumValue | null;
  agencyBannerStats: ProptreeEnumValue | null;
  // Bottom marquee that lists every agent in the brokerage. Same field
  // name as the agency page so the landing renderer treats both
  // proptree types identically.
  showAgentFeed: ProptreeEnumValue | null;
  // "Stay in the loop" newsletter signup card.
  newsletter: boolean;
};

function initialFormState(data: AffiliateSettings): FormState {
  return {
    alias: data.alias ?? '',
    profileImage: data.profileImage,
    profileFirstName: data.profileFirstName ?? '',
    profileSurName: data.profileSurName ?? '',
    profileCompany: data.profileCompany ?? '',
    buttons: data.buttons,
    externalAccess: data.externalAccess,
    whatsAppCountryCode: data.whatsAppCountryCode,
    whatsAppNumber: data.whatsAppNumber ?? '',
    mainFeed: data.mainFeed,
    propertiesType: data.propertiesType,
    property: data.property,
    // Bare projection of the API's `{ id, name }` into the enum shape so
    // the EnumSelect can render it before formOptions arrives. The form
    // reconciliation effect upgrades this to a full formOption match
    // (with form._id as value) once the list loads.
    feed: data.feed
      ? { id: data.feed.id, name: data.feed.name, value: data.feed.id }
      : null,
    chooseListings: data.chooseListings,
    listings: data.listings ?? {},
    partners: data.partners ?? {},
    bannerSlogans: data.bannerSlogans ?? defaultBannerSlogans(),
    showAgencyBanner: data.showAgencyBanner,
    agencyBannerStats: data.agencyBannerStats,
    showAgentFeed: data.showAgentFeed,
    newsletter: data.newsletter === true,
  };
}

// `set` and `handleUndo` use this signature so cards can stay generic —
// they don't need to know which key they're mutating beyond their own
// label.
type FormSet = <K extends keyof FormState>(key: K, value: FormState[K]) => void;

// Edit screen for a single affiliate (custom non-agency-default) proptree.
// Loads the doc via GET /api/proptrees/affiliates/:id, then hands it to
// SplitView which owns the editable form state, history, save, and undo.
// Per-section dirty/save buttons are gone — there's one global Save +
// Undo in the unsaved-changes banner instead, matching the agency
// settings page's behaviour.
export function AffiliateSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = React.useState<AffiliateSettings | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notFound, setNotFound] = React.useState(false);
  // Active forms in the org — drive the "Form" button-type picker and
  // the Listings card's lead-capture form select. Failures are non-fatal
  // (the picker just shows an empty dropdown).
  const [formOptions, setFormOptions] = React.useState<ProptreeEnumValue[]>([]);

  React.useEffect(() => {
    if (!id) return;
    let alive = true;
    setError(null);
    setNotFound(false);
    setData(null);
    client.proptrees
      .getAffiliate(id)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError((err as Error).message);
        }
      });
    client.forms
      .list({ status: 'active', limit: 100, sort: 'name', order: 'asc' })
      .then((res) => {
        if (!alive) return;
        setFormOptions(
          res.items.map((f, i) => ({
            id: i + 1,
            name: f.name ?? '(unnamed)',
            value: f._id,
          })),
        );
      })
      .catch(() => {
        // Swallow.
      });
    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Update affiliate
            </h1>
            <div className="mt-3 flex items-center gap-3">
              <Link
                to="/affiliate"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to affiliates
              </Link>
              <span className="text-muted-foreground/50">·</span>
              <div className="eyebrow">Affiliate</div>
            </div>
          </div>
          {data && (data.alias || data.shortId) && (
            <OpenProptreeCard alias={data.alias ?? data.shortId ?? ''} />
          )}
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}

        {notFound && (
          <Card className="p-6">
            <p className="font-sans text-[13px] text-muted-foreground">
              No affiliate proptree with this id in your organisation.
            </p>
          </Card>
        )}

        {!data && !error && !notFound && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">
            Loading…
          </Card>
        )}

        {data && (
          <SplitView
            data={data}
            formOptions={formOptions}
            onDataChange={setData}
          />
        )}
      </div>
    </AppShell>
  );
}

// Owns the editable form state, the undo history, and the save round-
// trip. Renders the form on the left and a sticky live preview on the
// right. Mirrors the agency settings page's `SplitView` structurally
// so the two surfaces feel like one product.
function SplitView({
  data,
  formOptions,
  onDataChange,
}: {
  data: AffiliateSettings;
  formOptions: ProptreeEnumValue[];
  onDataChange: (next: AffiliateSettings) => void;
}) {
  const [form, setForm] = React.useState<FormState>(() =>
    initialFormState(data),
  );
  // Last-saved snapshot. Drives the dirty flag (form ≠ original) and the
  // diff computation at save time. Wipe + reset whenever the upstream
  // doc changes (i.e. after a save).
  const [original, setOriginal] = React.useState<FormState>(() =>
    initialFormState(data),
  );
  // Stack of `form` snapshots, in chronological order. Each call to
  // `set` pushes the pre-edit form here so Undo can walk it back one
  // edit at a time. Cleared on save so we never undo across a save.
  const [history, setHistory] = React.useState<FormState[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Resync on data change — fired after a save replaces `data` with the
  // server's canonical version (which may have trimmed strings, minted
  // button ids, etc.). The post-save state is the new clean baseline.
  //
  // `alias` is the one exception: it lives outside the global save flow
  // (committed only via the IdentityCard's Check button), so we preserve
  // the user's in-flight typed value through the reset. If their typed
  // alias survives because the server doc still has the old alias, the
  // diff treats it as clean (alias is excluded) so the banner stays
  // hidden — exactly what the user wants when they save other fields.
  React.useEffect(() => {
    const fresh = initialFormState(data);
    setForm((prev) => ({ ...fresh, alias: prev.alias }));
    setOriginal(fresh);
    setHistory([]);
  }, [data]);

  // Reconcile the bare-projection `feed` from initialFormState against
  // the real formOptions once they arrive. Legacy `data.feed.id`
  // doesn't match Form._id, so fall back to name match. Also resync
  // `original` so dirty doesn't go true just because the reconciliation
  // upgraded the shape.
  React.useEffect(() => {
    if (formOptions.length === 0) return;
    const reconcile = (prev: FormState): FormState => {
      if (!prev.feed) return prev;
      const alreadyMatched = formOptions.some(
        (o) => o.value === prev.feed?.value,
      );
      if (alreadyMatched) return prev;
      const byName = formOptions.find(
        (o) =>
          (o.name ?? '').toLowerCase() ===
          String(prev.feed?.name ?? '').toLowerCase(),
      );
      if (!byName) return prev;
      return { ...prev, feed: byName };
    };
    setForm(reconcile);
    setOriginal(reconcile);
  }, [formOptions]);

  const set: FormSet = (key, value) => {
    if (JSON.stringify(form[key]) === JSON.stringify(value)) return;
    setHistory((h) => [...h, form]);
    setForm({ ...form, [key]: value });
  };

  const handleUndo = () => {
    const restored = history[history.length - 1];
    // Index access is `T | undefined` under noUncheckedIndexedAccess; the
    // length check alone doesn't narrow it.
    if (!restored) return;
    setForm(restored);
    setHistory(history.slice(0, -1));
  };
  // `alias` is excluded from the global dirty signal because it has its
  // own inline commit path on the IdentityCard. Compare every other
  // field instead — that way the unsaved-changes banner only appears
  // for things Save Changes will actually persist.
  const dirty = JSON.stringify({ ...form, alias: '' }) !==
    JSON.stringify({ ...original, alias: '' });
  // Undo is gated on the SAME signal as the banner: undo only makes
  // sense while the banner is visible. Otherwise a stray alias edit
  // would expose the Undo button without the banner appearing, which
  // looks broken. The alias has its own implicit "undo" — just retype.
  const canUndo = dirty && history.length > 0;

  const handleSave = async () => {
    if (saving || !dirty) return;
    const patch = buildDiffPatch(form, original);
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await client.proptrees.updateAffiliate(data._id, patch);
      onDataChange(updated);
      // The data-change effect above will reset form/original/history.
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {dirty && (
        <UnsavedChangesBanner
          onSave={handleSave}
          onUndo={handleUndo}
          canUndo={canUndo}
          saving={saving}
          saveError={saveError}
        />
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <IdentityCard
            form={form}
            set={set}
            shortId={data.shortId}
            docId={data._id}
            onAliasSaved={(savedAlias) => {
              // Inline-save success: snap `original.alias` to the saved
              // value so the diff sees alias as clean WITHOUT touching
              // other fields' dirty state. The user's other in-flight
              // edits stay dirty and the banner stays visible for them.
              setOriginal((prev) => ({ ...prev, alias: savedAlias }));
            }}
          />
          <ButtonsCard
            form={form}
            set={set}
            formOptions={formOptions}
            proptreeId={data._id}
          />
          <ListingsCard form={form} set={set} formOptions={formOptions} />
          <ManualListingsCard form={form} set={set} />
          <BannerCard form={form} set={set} />
          <ContactExternalCard form={form} set={set} />
        </div>
        <div className="lg:sticky lg:top-[calc(var(--header-h,4rem)+var(--banner-h,0px)+0.5rem)] lg:h-[calc(100dvh-var(--header-h,4rem)-var(--banner-h,0px)-1.5rem)]">
          <ProptreePreview
            slug={data.alias || data.shortId || null}
            patch={buildAffiliatePreviewPatch(form)}
          />
        </div>
      </div>
    </div>
  );
}

// Compute the minimal PUT body — only fields where form ≠ original go
// over the wire. Object-shape fields (enums, button arrays, listing
// maps) are compared via JSON; strings compare by ===.
//
// `alias` intentionally NOT included here: it has its own commit path
// (the "Check availability" button on IdentityCard which checks then
// inline-PUTs the alias on its own). Keeping alias out of the global
// diff means: a) the unsaved-changes banner doesn't appear for an
// alias-only edit, and b) clicking Save Changes for other dirty fields
// doesn't accidentally write through whatever the user has half-typed
// in the alias field. The alias-Check flow updates `original.alias` on
// success so the form stays consistent.
function buildDiffPatch(
  form: FormState,
  original: FormState,
): AffiliateSettingsUpdateInput {
  const patch: AffiliateSettingsUpdateInput = {};
  if (form.profileImage !== original.profileImage) {
    patch.profileImage = form.profileImage;
  }
  if (form.profileFirstName !== original.profileFirstName) {
    patch.profileFirstName = form.profileFirstName;
  }
  if (form.profileSurName !== original.profileSurName) {
    patch.profileSurName = form.profileSurName;
  }
  if (form.profileCompany !== original.profileCompany) {
    patch.profileCompany = form.profileCompany;
  }
  if (JSON.stringify(form.buttons) !== JSON.stringify(original.buttons)) {
    patch.profileButtons = form.buttons;
  }
  if (
    JSON.stringify(form.externalAccess) !==
    JSON.stringify(original.externalAccess)
  ) {
    patch.externalAccess = form.externalAccess;
  }
  if (
    JSON.stringify(form.whatsAppCountryCode) !==
    JSON.stringify(original.whatsAppCountryCode)
  ) {
    patch.whatsAppCountryCode = form.whatsAppCountryCode;
  }
  if (form.whatsAppNumber !== original.whatsAppNumber) {
    patch.whatsAppNumber = form.whatsAppNumber;
  }
  if (JSON.stringify(form.mainFeed) !== JSON.stringify(original.mainFeed)) {
    patch.mainFeed = form.mainFeed;
  }
  if (
    JSON.stringify(form.propertiesType) !==
    JSON.stringify(original.propertiesType)
  ) {
    patch.propertiesType = form.propertiesType;
  }
  if (JSON.stringify(form.property) !== JSON.stringify(original.property)) {
    patch.property = form.property;
  }
  // `feed` round-trips as legacy `{ id, name }` on the wire even though
  // the form holds it as a ProptreeEnumValue.
  const feedAsApi = form.feed
    ? {
        id: String(form.feed.value ?? ''),
        name: String(form.feed.name ?? ''),
      }
    : null;
  const origFeedAsApi = original.feed
    ? {
        id: String(original.feed.value ?? ''),
        name: String(original.feed.name ?? ''),
      }
    : null;
  if (JSON.stringify(feedAsApi) !== JSON.stringify(origFeedAsApi)) {
    patch.feed = feedAsApi;
  }
  if (
    JSON.stringify(form.chooseListings) !==
    JSON.stringify(original.chooseListings)
  ) {
    patch.chooseListings = form.chooseListings;
  }
  if (JSON.stringify(form.listings) !== JSON.stringify(original.listings)) {
    patch.listings = form.listings;
  }
  if (JSON.stringify(form.partners) !== JSON.stringify(original.partners)) {
    patch.partners = form.partners;
  }
  // Clean before diffing so whitespace-only customs don't make the form
  // look dirty between renders.
  const formBanner = cleanBannerSlogans(form.bannerSlogans);
  const origBanner = cleanBannerSlogans(original.bannerSlogans);
  if (JSON.stringify(formBanner) !== JSON.stringify(origBanner)) {
    patch.bannerSlogans = formBanner;
  }
  if (
    JSON.stringify(form.showAgencyBanner) !==
    JSON.stringify(original.showAgencyBanner)
  ) {
    patch.showAgencyBanner = form.showAgencyBanner;
  }
  if (
    JSON.stringify(form.agencyBannerStats) !==
    JSON.stringify(original.agencyBannerStats)
  ) {
    patch.agencyBannerStats = form.agencyBannerStats;
  }
  if (
    JSON.stringify(form.showAgentFeed) !==
    JSON.stringify(original.showAgentFeed)
  ) {
    patch.showAgentFeed = form.showAgentFeed;
  }
  if (form.newsletter !== original.newsletter) {
    patch.newsletter = form.newsletter;
  }
  return patch;
}

// Build a preview patch from the live `form` state. Drives the iframe
// re-render on every keystroke. Agency-only patch fields (banner / agents
// / partners / newsletter) stay undefined so the iframe falls back to
// its server-fetched values.
function buildAffiliatePreviewPatch(form: FormState): ProptreePreviewPatch {
  return {
    name: form.profileFirstName,
    image: form.profileImage,
    buttons: form.buttons
      .filter((b) => b.enabled)
      .map((b) => ({
        id: b.id,
        title: b.title,
        subtitle: b.subtitle,
        url: b.url,
        icon: b.icon,
        image: b.image,
        images: b.images,
        type: b.type,
      })),
    mainFeed: form.mainFeed?.value !== 'disabled',
    propertiesType:
      typeof form.propertiesType?.value === 'string'
        ? form.propertiesType.value
        : null,
    property:
      typeof form.property?.value === 'string' ? form.property.value : null,
    partners: form.partners,
    bannerSlogans: cleanBannerSlogans(form.bannerSlogans),
    agencyBanner: {
      visible: form.showAgencyBanner?.value === 'enabled',
      showStats: form.agencyBannerStats?.value === 'enabled',
    },
    showAgents: form.showAgentFeed?.value === 'enabled',
    newsletter: form.newsletter,
    // Manual listing picker — push the override so the iframe's
    // ListingsSection re-fetches with the right filter. When the picker
    // is off, send 'disabled' (clears any in-flight override).
    chooseListings:
      typeof form.chooseListings?.value === 'string'
        ? form.chooseListings.value
        : 'disabled',
    listings: Object.keys(form.listings).filter(
      (k) => form.listings[k] === true,
    ),
  };
}

// Sticky strip below the topbar that nudges the user to save. Visible
// while `form ≠ original`. Portals into AppShell's banner slot so it
// renders full-bleed across the viewport, exactly like the agency
// settings page. Undo steps back through the form-edit history one
// edit at a time.
function UnsavedChangesBanner({
  onSave,
  onUndo,
  canUndo,
  saving,
  saveError,
}: {
  onSave: () => void;
  onUndo: () => void;
  canUndo: boolean;
  saving: boolean;
  saveError: string | null;
}) {
  const slot = useBannerSlot();
  if (!slot) return null;
  return createPortal(
    <div className="border-b border-primary/40 bg-primary/10 text-foreground dark:bg-primary/20">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2 text-[13px]">
        <span className="flex-1">
          {saveError ? (
            <span className="font-mono text-[11px] text-destructive">
              {saveError}
            </span>
          ) : (
            <>
              You have unsaved changes. Save to publish to your live affiliate
              page.
            </>
          )}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onUndo}
          disabled={!canUndo || saving}
          title="Undo last change"
        >
          <Undo2 />
          Undo
        </Button>
        {isTestMode() ? (
          <Tooltip
            label="Can't save affiliate in test mode"
            className="cursor-not-allowed"
          >
            <Button type="button" size="sm" disabled>
              Save changes
            </Button>
          </Tooltip>
        ) : (
          <Button type="button" size="sm" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      </div>
    </div>,
    slot,
  );
}

// === Section cards =====================================================
//
// All cards are now controlled — they read from `form` and emit changes
// via `set`. None of them own dirty state, save buttons, or save errors;
// those live at the SplitView level so Save + Undo + the banner can
// reason about the whole form at once.

// Inline message shown next to the Check availability button. Colored
// by state — destructive for taken/invalid/error, emerald for saved,
// muted for idle/in-flight.
function AliasCheckStatus({ state }: { state: AliasCheckState }) {
  if (state.kind === 'idle') return null;
  const muted = state.kind === 'checking' || state.kind === 'saving';
  const taken =
    state.kind === 'taken' || state.kind === 'invalid' || state.kind === 'error';
  const saved = state.kind === 'saved';
  const text =
    state.kind === 'checking'
      ? 'Checking…'
      : state.kind === 'saving'
        ? 'Available — saving…'
        : state.kind === 'taken'
          ? 'Already in use — pick another shortname.'
          : state.kind === 'invalid'
            ? state.message
            : state.kind === 'error'
              ? state.message
              : 'Available — saved.';
  return (
    <span
      className={cn(
        'font-sans text-[12px]',
        muted && 'text-muted-foreground',
        taken && 'text-destructive',
        saved && 'text-emerald-600',
      )}
    >
      {text}
    </span>
  );
}

// Local state machine for the alias availability check + inline-save
// flow. Drives the helper text below the alias input.
type AliasCheckState =
  | { kind: 'idle' }
  | { kind: 'invalid'; message: string }
  | { kind: 'checking' }
  | { kind: 'taken' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

function IdentityCard({
  form,
  set,
  shortId,
  docId,
  onAliasSaved,
}: {
  form: FormState;
  set: FormSet;
  // Read-only `shortId` from the doc — surfaced as the alias-input
  // placeholder so the user can see what slug the public page falls
  // back to when alias is empty.
  shortId: string | null;
  // Doc id is needed for the alias-check exclude filter (so the doc
  // doesn't clash with itself) and for the inline PUT.
  docId: string;
  // Fired after a successful inline-save of just the alias field; the
  // page updates `original.alias` so the diff sees alias as clean
  // without resetting the rest of the form.
  onAliasSaved: (savedAlias: string) => void;
}) {
  const displayName =
    form.profileCompany ||
    [form.profileFirstName, form.profileSurName].filter(Boolean).join(' ') ||
    form.alias ||
    '—';
  const [aliasCheck, setAliasCheck] = React.useState<AliasCheckState>({
    kind: 'idle',
  });

  // Reset the check state whenever the user types — the previous
  // result no longer applies to the new value.
  React.useEffect(() => {
    setAliasCheck({ kind: 'idle' });
  }, [form.alias]);

  const checkAndSaveAlias = async () => {
    const value = form.alias.trim();
    // Empty alias is "valid" in the sense that the API accepts it
    // (clears the field) but there's nothing useful to check, so we
    // gate the button to non-empty values via `disabled` below. Be
    // defensive anyway.
    if (value === '') {
      setAliasCheck({ kind: 'invalid', message: 'Type a shortname first.' });
      return;
    }
    if (!/^[a-z0-9-]+$/.test(value)) {
      setAliasCheck({
        kind: 'invalid',
        message:
          'Only lowercase letters, digits, and dashes are allowed — no spaces.',
      });
      return;
    }
    setAliasCheck({ kind: 'checking' });
    try {
      const { available } = await client.proptrees.checkAffiliateAlias({
        alias: value,
        excludeId: docId,
      });
      if (!available) {
        setAliasCheck({ kind: 'taken' });
        return;
      }
      setAliasCheck({ kind: 'saving' });
      await client.proptrees.updateAffiliate(docId, { alias: value });
      onAliasSaved(value);
      setAliasCheck({ kind: 'saved' });
    } catch (err) {
      setAliasCheck({
        kind: 'error',
        message: (err as Error).message,
      });
    }
  };

  // Whether the Check button should be enabled.
  const canCheck =
    form.alias.trim() !== '' &&
    aliasCheck.kind !== 'checking' &&
    aliasCheck.kind !== 'saving';

  return (
    <SectionCard
      title="Identity"
      subtitle="Image, custom URL, and tagline"
      icon={UserIcon}
      defaultOpen
    >
      <div className="grid grid-cols-12 gap-4 p-5">
        <div className="col-span-12">
          <Label>Image</Label>
          <div className="mt-1 flex items-center gap-3">
            <ProfileAvatar image={form.profileImage} name={displayName} />
            <ImageUploadButton
              kind="proptree-profile"
              proptreeId={docId}
              onUploaded={(url) => set('profileImage', url)}
              label={form.profileImage ? 'Replace image' : 'Upload image'}
            />
          </div>
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Profile picture shown at the top of this affiliate's public page.
          </p>
        </div>

        <div className="col-span-12">
          <Label htmlFor="alias">
            Custom shortname{' '}
            <span className="ml-1 font-mono text-[10px] text-muted-foreground">
              (recommended)
            </span>
          </Label>
          <div className="mt-1 flex">
            <span className="inline-flex h-11 items-center rounded-l-md border border-r-0 border-border bg-muted px-3 font-mono text-[13px] text-muted-foreground">
              https://proptr.ee/
            </span>
            <Input
              id="alias"
              value={form.alias}
              onChange={(e) => set('alias', e.target.value)}
              placeholder={shortId ?? ''}
              className="rounded-l-none bg-muted/30"
            />
          </div>
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            The path under proptr.ee/… that opens this affiliate page.
            Lowercase letters, numbers, and dashes only.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={checkAndSaveAlias}
              disabled={!canCheck}
            >
              {aliasCheck.kind === 'checking'
                ? 'Checking…'
                : aliasCheck.kind === 'saving'
                  ? 'Saving…'
                  : 'Check availability'}
            </Button>
            <AliasCheckStatus state={aliasCheck} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 border-t border-[hsl(var(--hairline))] p-5">
        <div className="col-span-12">
          <Label htmlFor="tagline">Tagline</Label>
          <Input
            id="tagline"
            className="mt-1 bg-muted/30"
            value={form.profileFirstName}
            onChange={(e) => set('profileFirstName', e.target.value)}
          />
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Short line shown beneath the affiliate name on the public page.
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

// Action buttons section. Two-tab layout — "Personal buttons" (the
// editable list of profile buttons) and "Partner buttons" (pre-configured
// branded toggles like Mortgage Finder). Mirrors the agency settings
// ButtonsSection so the manager-portal UX is identical across surfaces.
function ButtonsCard({
  form,
  set,
  formOptions,
  proptreeId,
}: {
  form: FormState;
  set: FormSet;
  formOptions: ProptreeEnumValue[];
  // Owning proptree id — threaded down to ProptreeButtonRow + the
  // AddProptreeButtonModal so button image uploads can be tagged
  // with the right doc id on DO Spaces.
  proptreeId: string;
}) {
  const [tab, setTab] = React.useState<'personal' | 'partners'>('personal');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [draggingIndex, setDraggingIndex] = React.useState<number | null>(null);

  const updateAt = (i: number, patch: Partial<ProfileButton>) =>
    set(
      'buttons',
      form.buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)),
    );
  const removeAt = (i: number) =>
    set(
      'buttons',
      form.buttons.filter((_, j) => j !== i),
    );

  const handleDrop = (target: number) => {
    if (draggingIndex == null || draggingIndex === target) {
      setDraggingIndex(null);
      return;
    }
    const next = [...form.buttons];
    const [moved] = next.splice(draggingIndex, 1);
    // splice returns `T[]`, so the destructured element is `T | undefined`.
    if (moved) {
      next.splice(target, 0, moved);
      set('buttons', next);
    }
    setDraggingIndex(null);
  };

  return (
    <SectionCard
      title="Buttons"
      subtitle="Call-to-action and partner buttons"
      icon={MousePointerClick}
    >
      <div className="p-5">
        <div className="inline-flex rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
          <SectionTabButton
            active={tab === 'personal'}
            onClick={() => setTab('personal')}
          >
            Personal buttons
          </SectionTabButton>
          <SectionTabButton
            active={tab === 'partners'}
            onClick={() => setTab('partners')}
          >
            Partner buttons
          </SectionTabButton>
        </div>

        {tab === 'personal' ? (
          <>
            <Label className="mt-3 block">Buttons</Label>
            {form.buttons.length === 0 ? (
              <div className="mt-1 rounded-md border border-dashed border-border bg-card/40 p-6 text-center font-sans text-[12px] text-muted-foreground">
                No buttons yet.
              </div>
            ) : (
              <div className="mt-1 space-y-2">
                {form.buttons.map((b, i) => (
                  <ProptreeButtonRow
                    key={b.id}
                    index={i}
                    button={b}
                    isDragging={draggingIndex === i}
                    formOptions={formOptions}
                    proptreeId={proptreeId}
                    onChange={(patch) => updateAt(i, patch)}
                    onRemove={() => removeAt(i)}
                    onDragStart={() => setDraggingIndex(i)}
                    onDragEnd={() => setDraggingIndex(null)}
                    onDrop={() => handleDrop(i)}
                  />
                ))}
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 bg-muted hover:bg-muted-foreground/20"
              onClick={() => setModalOpen(true)}
              disabled={form.buttons.length >= BUTTONS_MAX}
            >
              <Plus />
              Create new button
            </Button>
            <p className="mt-2 font-mono text-[10px] text-muted-foreground">
              {form.buttons.length}/{BUTTONS_MAX}
            </p>
            <AddProptreeButtonModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              onAdd={(button) => set('buttons', [...form.buttons, button])}
              formOptions={formOptions}
              proptreeId={proptreeId}
            />
          </>
        ) : (
          <PartnerToggleList
            value={form.partners}
            onChange={(next) => set('partners', next)}
          />
        )}
      </div>
    </SectionCard>
  );
}

// Two-state tab button matching the agency settings page's tabs strip.
// Kept local to the affiliate page for now — duplication with the agency
// version is the same component but extracting cross-page is a follow-up.
function SectionTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// Listings filters — mainFeed master switch, propertiesType / property
// subsidiary enums, and the lead-capture form picker. The manual
// listing picker (`chooseListings` + `listings` map) lives in its own
// card so this stays focused on filter dimensions.
function ListingsCard({
  form,
  set,
  formOptions,
}: {
  form: FormState;
  set: FormSet;
  formOptions: ProptreeEnumValue[];
}) {
  const mainFeedEnabled = form.mainFeed?.value !== 'disabled';

  return (
    <SectionCard
      title="Listings"
      subtitle="Feed filters and lead-capture form"
      icon={Home}
    >
      <div className="p-5">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-6">
            <Label>Show listings</Label>
            <SwitchBox
              checked={mainFeedEnabled}
              onChange={(v) =>
                set(
                  'mainFeed',
                  MAIN_FEED_OPTIONS.find(
                    (o) => o.value === (v ? 'enabled' : 'disabled'),
                  ) ??
                    MAIN_FEED_OPTIONS[0] ??
                    null,
                )
              }
              ariaLabel="Show listings"
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              Renders the listings feed on this affiliate's page.
            </p>
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Label>Show agents</Label>
            <SwitchBox
              checked={form.showAgentFeed?.value === 'enabled'}
              onChange={(v) =>
                set(
                  'showAgentFeed',
                  AGENCY_BANNER_OPTIONS.find(
                    (o) => o.value === (v ? 'enabled' : 'disabled'),
                  ) ?? null,
                )
              }
              ariaLabel="Show agents"
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              Bottom marquee that lists every agent in the brokerage.
            </p>
          </div>
          {mainFeedEnabled && (
            <>
              <div className="col-span-12 sm:col-span-6 sm:col-start-1">
                <Label>Type</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={PROPERTIES_TYPE_OPTIONS}
                  value={form.propertiesType}
                  onChange={(v) => set('propertiesType', v)}
                  placeholder="Click to Select"
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Restrict the feed to sale-only, rental-only, or both.
                </p>
              </div>
              <div
                className={cn(
                  'col-span-12 sm:col-span-6',
                  !form.propertiesType && 'opacity-50',
                )}
              >
                <Label>Plan</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={PROPERTY_PLAN_OPTIONS}
                  value={form.property}
                  onChange={(v) => set('property', v)}
                  placeholder="Click to Select"
                  disabled={!form.propertiesType}
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Restrict the feed to on-plan, off-plan, or both.
                </p>
              </div>
              <div className="col-span-12 sm:col-span-6 sm:col-start-1">
                <Label>Qualify leads (listing clicks)</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={formOptions}
                  value={form.feed}
                  onChange={(v) => set('feed', v)}
                  placeholder="Select a form"
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Form shown to visitors before their listing click reaches
                  the affiliate.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

// Manual listing picker — custom-only. When chooseListings is enabled,
// the public affiliate page only renders listings whose externalId is
// truthy in the `listings` map. The picks block sits on its own full-
// width row below the switch and renders a thumbnail grid of the picked
// listings so the manager can eyeball selection without opening the
// modal.
function ManualListingsCard({
  form,
  set,
}: {
  form: FormState;
  set: FormSet;
}) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const chooseEnabled = form.chooseListings?.value === 'enabled';
  const pickedIds = React.useMemo(
    () => Object.keys(form.listings).filter((k) => form.listings[k] === true),
    [form.listings],
  );
  const pickedCount = pickedIds.length;

  // Thumbnail cache keyed by externalId. Fetched on demand when the
  // picker is enabled and there are picks to show — bypasses the modal,
  // so the thumbnails are visible even before the user opens it. The
  // /api/listings route bypasses status filtering when externalIds is
  // supplied, so archived picks still resolve.
  const [thumbCache, setThumbCache] = React.useState<
    Record<string, ListingRow>
  >({});

  React.useEffect(() => {
    if (!chooseEnabled) return;
    const missing = pickedIds.filter((id) => !thumbCache[id]);
    if (missing.length === 0) return;
    let alive = true;
    client.listings
      .list({ externalIds: missing.join(','), limit: 100 })
      .then((res) => {
        if (!alive) return;
        setThumbCache((prev) => {
          const next = { ...prev };
          for (const row of res.items) {
            if (row.externalId) next[row.externalId] = row;
          }
          return next;
        });
      })
      .catch(() => {
        // Best-effort — thumbnails fall back to a grey square if the
        // fetch fails.
      });
    return () => {
      alive = false;
    };
  }, [chooseEnabled, pickedIds, thumbCache]);

  return (
    <SectionCard
      title="Listing selection"
      subtitle="Show all matching listings or pick a custom set"
      icon={Filter}
    >
      <div className="grid grid-cols-12 gap-4 p-5">
        <div className="col-span-12 sm:col-span-6">
          <Label>Listing selection</Label>
          <SwitchBox
            checked={chooseEnabled}
            onChange={(v) =>
              set(
                'chooseListings',
                CHOOSE_LISTINGS_OPTIONS.find(
                  (o) => o.value === (v ? 'enabled' : 'disabled'),
                ) ?? null,
              )
            }
            ariaLabel="Manual listing selection"
          />
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            {chooseEnabled
              ? 'Only the listings picked below show on the public page.'
              : 'All matching listings show automatically. Turn on to pick specific listings.'}
          </p>
        </div>
        {chooseEnabled && (
          <div className="col-span-12">
            <Label>Picked listings</Label>
            <div className="mt-1 rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[12px] text-foreground/80 tabular-nums">
                  {pickedCount} selected
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPickerOpen(true)}
                >
                  <Search />
                  Pick listings…
                </Button>
              </div>
              {pickedCount > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(() => {
                    // Cap at THUMB_MAX visible. When the user has more picks,
                    // the last slot becomes a "+N" overflow tile showing how
                    // many extras are hidden, matching common pick-grid
                    // patterns (Slack reactions, photo galleries, etc.).
                    const THUMB_MAX = 10;
                    const visible =
                      pickedCount <= THUMB_MAX
                        ? pickedIds
                        : pickedIds.slice(0, THUMB_MAX - 1);
                    const overflow =
                      pickedCount > THUMB_MAX
                        ? pickedCount - (THUMB_MAX - 1)
                        : 0;
                    return (
                      <>
                        {visible.map((id) => {
                          const row = thumbCache[id];
                          return (
                            <div
                              key={id}
                              title={
                                row?.title ? `${row.title} · ${id}` : id
                              }
                              className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md border border-border bg-muted"
                            >
                              {row?.image ? (
                                <img
                                  src={row.image}
                                  alt={row.title ?? id}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center px-0.5 text-center font-mono text-[8px] uppercase leading-tight text-muted-foreground">
                                  {id}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {overflow > 0 && (
                          <button
                            type="button"
                            onClick={() => setPickerOpen(true)}
                            title={`${overflow} more — click to view`}
                            className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/60 font-mono text-[11px] font-semibold text-foreground/80 transition-colors hover:bg-muted"
                          >
                            +{overflow}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              {pickedCount === 0 && (
                <p className="mt-3 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Use “Pick listings…” to choose which listings show on the
                  public page.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <ListingPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initialPicks={form.listings}
        onCommit={(picks) => {
          set('listings', picks);
          setPickerOpen(false);
        }}
      />
    </SectionCard>
  );
}

// Modal: search + grid of the enterprise's active listings, each with a
// checkbox. Local working-copy of the picks map so Cancel can discard
// in-flight changes without polluting the parent form. Listings already
// picked but no longer in the active feed (e.g. archived) are merged in
// as synthetic rows so the user can still see and remove them.
function ListingPickerModal({
  open,
  onClose,
  initialPicks,
  onCommit,
}: {
  open: boolean;
  onClose: () => void;
  initialPicks: Record<string, boolean>;
  onCommit: (picks: Record<string, boolean>) => void;
}) {
  const [picks, setPicks] =
    React.useState<Record<string, boolean>>(initialPicks);
  const [search, setSearch] = React.useState('');
  const [items, setItems] = React.useState<ListingRow[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [shown, setShown] = React.useState(open);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      setPicks(initialPicks);
      setSearch('');
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(r2);
      });
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open, onClose, initialPicks]);

  React.useEffect(() => {
    if (!open) return;
    let alive = true;
    setError(null);
    setItems(null);
    client.listings
      .list({ status: 'active', limit: 100 })
      .then((res) => {
        if (!alive) return;
        setItems(res.items);
      })
      .catch((err: unknown) => {
        if (alive) setError((err as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  const visibleItems: Array<{
    row: ListingRow | null;
    externalId: string;
    inFeed: boolean;
  }> = React.useMemo(() => {
    const out: Array<{
      row: ListingRow | null;
      externalId: string;
      inFeed: boolean;
    }> = [];
    const seen = new Set<string>();
    if (items) {
      for (const r of items) {
        const ext = r.externalId ?? '';
        if (!ext) continue;
        out.push({ row: r, externalId: ext, inFeed: true });
        seen.add(ext);
      }
    }
    for (const ext of Object.keys(picks)) {
      if (picks[ext] === true && !seen.has(ext)) {
        out.push({ row: null, externalId: ext, inFeed: false });
      }
    }
    const q = search.trim().toLowerCase();
    if (!q) return out;
    return out.filter(({ row, externalId }) => {
      if (externalId.toLowerCase().includes(q)) return true;
      const title = row?.title?.toLowerCase() ?? '';
      const area = row?.area?.toLowerCase() ?? '';
      return title.includes(q) || area.includes(q);
    });
  }, [items, picks, search]);

  const togglePick = (externalId: string) => {
    setPicks((prev) => {
      const next = { ...prev };
      if (next[externalId]) delete next[externalId];
      else next[externalId] = true;
      return next;
    });
  };
  const pickedCount = Object.values(picks).filter((v) => v === true).length;

  if (!shown) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
        style={{ maxHeight: '80vh' }}
      >
        <div className="flex items-center justify-between border-b border-[hsl(var(--hairline))] px-5 py-4">
          <div>
            <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
              Pick listings
            </h2>
            <p className="mt-1.5 font-sans text-[12px] text-muted-foreground">
              Select which listings appear on the public affiliate page.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X />
          </Button>
        </div>

        <div className="border-b border-[hsl(var(--hairline))] px-5 py-3">
          <Input
            type="search"
            placeholder="Search title, area, or reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-muted/30"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
              {error}
            </div>
          ) : items === null ? (
            <div className="px-2 py-6 text-center font-mono text-[11px] text-muted-foreground">
              Loading…
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="px-2 py-6 text-center font-mono text-[11px] text-muted-foreground">
              No listings match.
            </div>
          ) : (
            <ul className="space-y-2">
              {visibleItems.map(({ row, externalId, inFeed }) => {
                const checked = picks[externalId] === true;
                return (
                  <li key={externalId}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-md border border-border bg-card/40 px-3 py-2 transition-colors hover:bg-muted/40',
                        checked && 'border-primary/50 bg-primary/5',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePick(externalId)}
                        className="h-4 w-4 shrink-0 accent-primary"
                      />
                      {row?.image ? (
                        <img
                          src={row.image}
                          alt=""
                          className="h-12 w-12 shrink-0 rounded-md bg-muted object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 shrink-0 rounded-md bg-muted" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-sans text-[13px] font-medium text-foreground">
                          {row?.title || externalId}
                        </div>
                        <div className="truncate font-mono text-[11px] text-muted-foreground">
                          {externalId}
                          {row?.area ? ` · ${row.area}` : ''}
                          {!inFeed && (
                            <span className="ml-2 inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
                              Not in active feed
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[hsl(var(--hairline))] px-5 py-3">
          <div className="font-mono text-[11px] text-muted-foreground">
            {pickedCount} selected
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => onCommit(picks)}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Three-tab banner section. Mirrors the agency settings page's
// MarqueeSection: "Banner · Slogans" (top marquee strip), "Banner ·
// Agency" (decorative card between listings), and "Newsletter" (signup
// card). Each tab is self-contained; only its own controls render.
function BannerCard({ form, set }: { form: FormState; set: FormSet }) {
  const [tab, setTab] = React.useState<BannerTab>('top');
  const topEnabled = form.bannerSlogans.enabled;
  const agencyBannerEnabled = form.showAgencyBanner?.value === 'enabled';

  return (
    <SectionCard
      title="Banners & newsletter"
      subtitle="Top marquee, agency card, and email signup"
      icon={Megaphone}
    >
      <div className="p-5">
        <div className="inline-flex rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
          {(Object.keys(BANNER_TAB_LABELS) as BannerTab[]).map((key) => (
            <SectionTabButton
              key={key}
              active={tab === key}
              onClick={() => setTab(key)}
            >
              {BANNER_TAB_LABELS[key]}
            </SectionTabButton>
          ))}
        </div>

        {tab === 'top' && (
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 sm:col-span-6">
              <Label>Show top banner</Label>
              <SwitchBox
                checked={topEnabled}
                onChange={(v) =>
                  set('bannerSlogans', { ...form.bannerSlogans, enabled: v })
                }
                ariaLabel="Enable top section banner"
              />
              <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                Marquee strip that rotates slogans above the affiliate page.
              </p>
            </div>
            {topEnabled && (
              <div className="col-span-12">
                <BannerSlogansEditor
                  value={form.bannerSlogans}
                  onChange={(next) => set('bannerSlogans', next)}
                  propertiesType={form.propertiesType}
                  mainFeed={form.mainFeed}
                  showAgentFeed={form.showAgentFeed}
                />
              </div>
            )}
          </div>
        )}

        {tab === 'card' && (
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 sm:col-span-6">
              <Label>Show agency banner</Label>
              <SwitchBox
                checked={agencyBannerEnabled}
                onChange={(v) =>
                  set(
                    'showAgencyBanner',
                    AGENCY_BANNER_OPTIONS.find(
                      (o) => o.value === (v ? 'enabled' : 'disabled'),
                    ) ?? null,
                  )
                }
                ariaLabel="Show agency banner"
              />
              <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                Decorative card rendered between groups of listings on the
                public page.
              </p>
            </div>
            {agencyBannerEnabled && (
              <div className="col-span-12 sm:col-span-6">
                <Label>Banner stats</Label>
                <EnumSelect
                  className="mt-1 bg-muted/30"
                  options={BANNER_STATS_OPTIONS}
                  value={form.agencyBannerStats}
                  onChange={(v) => set('agencyBannerStats', v)}
                />
                <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                  Show or hide the active-listings count line on the agency
                  banner.
                </p>
              </div>
            )}
            <div className="col-span-12">
              <p className="font-sans text-[12px] leading-relaxed text-muted-foreground">
                <span className="mr-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/70">
                  OBS
                </span>
                Banner will show after the first “Load more” click, and only
                if more than 8 listings exist.
              </p>
            </div>
          </div>
        )}

        {tab === 'newsletter' && (
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 sm:col-span-6">
              <Label>Show newsletter card</Label>
              <SwitchBox
                checked={form.newsletter}
                onChange={(v) => set('newsletter', v)}
                ariaLabel="Enable newsletter card"
              />
              <p className="mt-1.5 font-sans text-[12px] leading-relaxed text-muted-foreground">
                Renders the “Stay in the loop” email-signup card on the
                public affiliate page.
              </p>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

// External-access + WhatsApp section. Country code and number gray out
// when external access is off, matching the agency page's
// disabled-when-off behaviour.
function ContactExternalCard({
  form,
  set,
}: {
  form: FormState;
  set: FormSet;
}) {
  const externalEnabled = form.externalAccess?.value === 'enabled';
  const testMode = isTestMode();
  return (
    <SectionCard
      title="External access"
      subtitle="Self-serve login via WhatsApp"
      icon={KeyRound}
    >
      <div className="grid grid-cols-12 gap-4 p-5">
        <div className="col-span-12 sm:col-span-6">
          <Label>External access</Label>
          {/* Grants a real sign-in to the proptree, so it stays locked
              in test mode — the seeded affiliates have no account to
              sign in to, and the toggle shouldn't imply otherwise. */}
          {testMode ? (
            <Tooltip
              label="Can't enable external access in test mode"
              className="block w-full cursor-not-allowed"
            >
              <div className="w-full">
                <SwitchBox
                  checked={externalEnabled}
                  onChange={() => undefined}
                  ariaLabel="External access"
                  disabled
                />
              </div>
            </Tooltip>
          ) : (
            <SwitchBox
              checked={externalEnabled}
              onChange={(v) =>
                set(
                  'externalAccess',
                  EXTERNAL_ACCESS_OPTIONS.find(
                    (o) => o.value === (v ? 'enabled' : 'disabled'),
                  ) ?? null,
                )
              }
              ariaLabel="External access"
            />
          )}
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Lets the affiliate sign in to this proptree like a regular
            proptree user, using the WhatsApp number below.
          </p>
        </div>
        <div
          className={cn(
            'col-span-12 sm:col-span-6 sm:col-start-1',
            !externalEnabled && 'opacity-50',
          )}
        >
          <Label>Country code</Label>
          <EnumSelect
            className="mt-1 bg-muted/30"
            options={COUNTRY_CODES}
            value={form.whatsAppCountryCode}
            onChange={(v) => set('whatsAppCountryCode', v)}
            placeholder="Click to Select"
            disabled={!externalEnabled}
          />
        </div>
        <div
          className={cn(
            'col-span-12 sm:col-span-6',
            !externalEnabled && 'opacity-50',
          )}
        >
          <Label htmlFor="whatsapp">
            WhatsApp{' '}
            <span className="ml-1 font-mono text-[10px] text-muted-foreground">
              (used for login)
            </span>
          </Label>
          <Input
            id="whatsapp"
            className="mt-1 bg-muted/30"
            value={form.whatsAppNumber}
            onChange={(e) => set('whatsAppNumber', e.target.value)}
            disabled={!externalEnabled}
          />
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Number the affiliate uses to sign in to this proptree.
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

// On/off pill — matches the agency settings styling so the visual
// language stays consistent across proptree-settings surfaces.
function SwitchBox({
  checked,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        'mt-1 flex h-11 items-center justify-between rounded-md border border-border bg-muted/30 px-3',
        disabled && 'opacity-60',
      )}
    >
      <span className="font-sans text-[13px] text-foreground">
        {checked ? 'Enabled' : 'Disabled'}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
          checked ? 'bg-primary' : 'bg-muted-foreground/30',
          disabled && 'cursor-not-allowed',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
          )}
        />
      </button>
    </div>
  );
}

// Adapter from legacy `{ id, name, value }` shape to our generic Select.
// `value` is the string we round-trip; we re-attach the original tuple
// (with id/name) on change so legacy-shaped saves can ship unchanged.
function EnumSelect({
  options,
  value,
  onChange,
  placeholder = 'Click to Select',
  disabled,
  className,
}: {
  options: ProptreeEnumValue[];
  value: ProptreeEnumValue | null;
  onChange: (v: ProptreeEnumValue) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const items = React.useMemo<SelectOption<string>[]>(
    () =>
      options
        .filter((o) => o.value != null)
        .map((o) => ({ value: String(o.value), label: o.name ?? '—' })),
    [options],
  );
  const current = value?.value != null ? String(value.value) : null;
  const searchable = options.length > 5;

  return (
    <Select<string>
      options={items}
      value={current}
      onChange={(next) => {
        const orig = options.find(
          (o) => o.value != null && String(o.value) === next,
        );
        if (orig) onChange(orig);
      }}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      searchable={searchable}
    />
  );
}

// Header chip on the right of the page heading. Mirrors the agency
// settings version verbatim; renders the public URL + an Open button.
function OpenProptreeCard({ alias }: { alias: string }) {
  const url = `${PUBLIC_URL}/${alias}`;
  // Seeded affiliates were never saved to the database, so the bare
  // public URL 404s. In test mode open the demo view instead — the
  // displayed URL stays clean so it still reads as the real address.
  const href = withDemoFlag(url);
  return (
    <Card className="flex w-[300px] items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-sans text-[13px] font-semibold leading-tight text-foreground">
          Affiliate proptree
        </div>
        <p className="mt-1 truncate font-sans text-[11px] leading-snug text-muted-foreground">
          {url.replace(/^https?:\/\//, '')}
        </p>
      </div>
      <Button asChild size="sm" variant="outline">
        <a href={href} target="_blank" rel="noopener noreferrer">
          <ExternalLink />
          Open
        </a>
      </Button>
    </Card>
  );
}

function ProfileAvatar({
  image,
  name,
}: {
  image: string | null;
  name: string;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-12 w-12 shrink-0 rounded-md bg-muted object-cover"
      />
    );
  }
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-[11px] font-bold uppercase">
      {initials(name)}
    </div>
  );
}
